import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    Link,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Slide,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from '../../reducers';
import {
    createNewMemberAssessment,
    deleteAssessmentDrafts,
    getEngagementDraftPreview,
    handleCloseSignConsentModal,
    resetDocumentEngagement,
    setEngagementDraftPreviews,
    toggleIsOpenAISessionId,
    toggleIsOpenSavedDraftsModel,
    toggleOpenSignConsentModel,
} from '../../store/documentEngagement.slice';
import { Role } from '../../Models/Role/Role.model';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { copyObject } from '../../utils/common';
import { isValid, differenceInDays, isBefore, isAfter, addDays } from 'date-fns';
import { toDate, formatDate } from '../../utils/dateHelpers';
import Authorization from '../../utils/Authorization';
import { fetchMemberDemoGraphicInfo, getMemberById } from '../../store/memberDetail.slice';
import { fetchRoles, resetAssessment, setSelectedRole } from '../../store/assessment.slice';
import AssessmentHistory from './History/History';
import { TransitionProps } from '@mui/material/transitions';
import classes from './Styles';
import { resetHraAssessment } from '../../store/hra.slice';
import { resetFuhFumAssessment } from '../../store/fuhfum.slice';
import {
    fetchPHQ9ByMemberId,
    getLastCompletedBHDiagnosticByMemberId,
    getConsentFormDocumentByMemberId,
    getMemberRequests,
    getMemberSurveys,
} from '../../store/assessmentDashboardSlice.slice';
import {
    applyAISession,
    fetchHeidiContextNotes,
    fetchMemberAiSummaryForContext,
    fetchMemberJourneySummaryForContext,
    HeidiContextNoteItem,
    launchHeidiWidget,
} from '../../store/documentEngagement.slice';
import { GenderEnum } from '../../Enum/GenderEnum';
import { ReviewHistory } from '../../Models/CarePlans/ReviewHistory.model';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import { StatusEnum } from '../../Enum/StatusEnum';
import AlertDialog from '../AlertDialog/AlertDialog';
import { getMemberByMemberId, hedisMetricsList } from '../../store/memberDemographicsDetails.slice';
import { ConsentDocumentTypeEnum } from '../../Enum/ConsentDocumentTypeEnum';
import { PriorityEnum } from '../../Enum/PriorityEnum';
import { roleEnum } from '../../utils/assessments';
import { MemberStatusEnum } from '../../Enum/MemberStatusEnum';
import { TimelineTypeEnum } from '../../Enum/TimelineTypeEnum';
import { insertTimeline } from '../../store/timeline.slice';
import { HedisMetricStatusEnum } from '../../Enum/HedisMetricStatusEnum';
import { DiagnosticType } from '../../Enum/DiagnosticType';
import InfoIcon from '@mui/icons-material/Info';
import BHDHelp from '../../assets/documents/BHD_Help.pdf';
import MessageDisplay from '../Shared/MessageDisplay';
import Loading from '../Shared/Loading';
import Consent from './Consent/Consent';
import { getCarePlanSignatureStatus } from '../../store/carePlanUpdate.slice';
import { AssessmentDashboardEnum } from '../../Enum/AssessmentDashboardEnum';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="down" ref={ref} {...props} />;
});
const RequestListItem = (props: any) => {
    const { request } = props;
    const priorityColor = request.Priority === PriorityEnum.High ? 'error' : request.Priority === PriorityEnum.Medium ? 'warning' : 'info';
    const statusColor = request.Status === StatusEnum.New ? 'info' : request.Status === StatusEnum.InProgress ? 'warning' : 'success';
    return (
        <Box sx={{ padding: 2, textAlign: 'center' }}>
            <Stack alignItems={'center'} spacing={1}>
                <Stack direction={'row'} spacing={1} alignItems={'center'} justifyContent={'space-between'} width={'100%'}>
                    <Chip size="small" label={PriorityEnum[request.Priority]} color={priorityColor} style={{ marginLeft: '5px' }} />
                    <Chip size="small" label={StatusEnum[request.Status]} color={statusColor} style={{ marginLeft: '5px' }} />
                </Stack>
                <Typography variant="body2">{request.Message}</Typography>
                <Typography variant="body2" fontStyle={'italic'}>
                    From: {request?.RequestFrom?.Name ? request?.RequestFrom?.Name : ' - '}
                </Typography>
                <Typography variant="body2" fontStyle={'italic'}>
                    To: {request?.RequestTo?.Name ? request?.RequestTo?.Name : ' - '}
                </Typography>
            </Stack>
        </Box>
    );
};

const AssessmentDashboard = () => {
    const dispatch = useAppDispatch();
    const params = useParams();
    const navigate = useNavigate();
    const memberId = params.memberId;
    const authData = new Authorization();

    const { bhDiagnostic, completedSurveys, consentDocumentValid, isFetchingCarePlanStatus, isFetchingMemberPHQ9, memberPHQ9, memberRequests } = useSelector(
        (state: RootState) => state.assessmentDashboardSlice
    );
    const {
        engagementDraftPreviews,
        isOpenSavedDraftsModel,
        isOpenSignConsentModel,
        isOpenAISessionId,
        heidiApplyStatus,
        heidiApplyProgressPercentage,
        heidiApplyLastCompletedAssessment,
        heidiApplyCompletedAssessments,
        heidiApplyTotalAssessments,
    } = useSelector((state: RootState) => state.documentEngagement);
    const { carePlanSignatureStatus } = useSelector((state: RootState) => state.carePlanUpdate);

    const { chartDetails } = useSelector((state: RootState) => state.memberDemographicsDetails);
    const { roles, selectedRole } = useSelector((state: RootState) => state.assessment);
    const openRequests = memberRequests.filter((r) => r.Status === StatusEnum.InProgress || r.Status === StatusEnum.New);

    const [isFetched, setIsFetched] = useState<boolean>(false);

    const [roleList, setRoleList] = useState<Role[]>([]);
    const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);
    const isMultiDraftsSelected = engagementDraftPreviews.filter((e) => e.IsSelected).length > 1;
    const [showHistory, setShowHistory] = useState<boolean>(false);
    const [selectedEngagementDraftId, setSelectedEngagementDraftId] = useState<string>('');
    const [showStatusChangeAlert, setShowStatusChangeAlert] = useState<boolean>(false);
    const [selectedRoleId, setSelectedRoleId] = useState<string>('');
    const [AISessionId, setAISessionId] = useState<string>('');

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isHeidiWidgetLoading, setIsHeidiWidgetLoading] = useState<boolean>(false);
    const [isHeidiAiWizardOpen, setIsHeidiAiWizardOpen] = useState<boolean>(false);
    const [heidiAiStartPath, setHeidiAiStartPath] = useState<'existing' | 'record' | ''>('');
    const [heidiAiStep, setHeidiAiStep] = useState<'choose' | 'existing' | 'record' | null>(null);
    const [isHeidiContextOptionsOpen, setIsHeidiContextOptionsOpen] = useState<boolean>(false);
    const [isHeidiContextOptionsLoading, setIsHeidiContextOptionsLoading] = useState<boolean>(false);
    const [heidiContextNotes, setHeidiContextNotes] = useState<HeidiContextNoteItem[]>([]);
    const [heidiContextNotesSource, setHeidiContextNotesSource] = useState<string>('');
    const [heidiContextJourney, setHeidiContextJourney] = useState<string>('');
    const [heidiContextAiSummary, setHeidiContextAiSummary] = useState<string>('');
    const [selectedHeidiNoteIds, setSelectedHeidiNoteIds] = useState<string[]>([]);
    const [includeMemberJourney, setIncludeMemberJourney] = useState<boolean>(false);
    const [includeAiMemberSummary, setIncludeAiMemberSummary] = useState<boolean>(false);
    const heidiContextRequestIdRef = useRef(0);
    const isHeidiApplyInProgress = heidiApplyStatus === 'Queued' || heidiApplyStatus === 'InProgress';

    useEffect(() => {
        if (!isHeidiWidgetLoading) {
            return;
        }
        document.body.style.cursor = 'wait';
        return () => {
            document.body.style.cursor = '';
        };
    }, [isHeidiWidgetLoading]);

    const [diagnosticType, setDiagnosticType] = useState<DiagnosticType>(DiagnosticType.None);
    const [assessmentType, setAssessmentType] = useState<AssessmentDashboardEnum>(AssessmentDashboardEnum.Assessment);

    const memberIsValidStatus = () => {
        return (
            chartDetails &&
            (chartDetails.Status === MemberStatusEnum.Active ||
                chartDetails.Status === MemberStatusEnum.UncompensatedCare ||
                chartDetails.Status === MemberStatusEnum.PreparingForGraduation ||
                chartDetails.Status === MemberStatusEnum.LostInsuranceCoverage)
        );
    };

    const handleAssessmentTypeChange = (event: any) => {
        const selectedType = Number(event.target.value) as AssessmentDashboardEnum;
        setAssessmentType(selectedType);
        if (selectedType === AssessmentDashboardEnum.BHDiagnostic || selectedType === AssessmentDashboardEnum.BHDiagnosticAI) {
            setDiagnosticType(DiagnosticType.BHDiagnostic);
        } else {
            setDiagnosticType(DiagnosticType.None);
        }
    };

    // Consent
    const [partOneDocument, setPartOneDocument] = useState(null);
    const [partTwoDocument, setPartTwoDocument] = useState(null);

    const handleOpenConsent = () => {
        dispatch(toggleOpenSignConsentModel(true));
    };

    useEffect(() => {
        if (consentDocumentValid?.length > 0) {
            let par1Document = consentDocumentValid?.find((d) => d.ConsentDocumentType === ConsentDocumentTypeEnum.PartOne);
            let part2Document = consentDocumentValid?.find((d) => d.ConsentDocumentType === ConsentDocumentTypeEnum.PartTwo);
            setPartOneDocument(par1Document);
            setPartTwoDocument(part2Document);
        }
    }, [consentDocumentValid]);

    // HEDIS
    const prepareHedisDetail = () => {
        if (Boolean(chartDetails?.HedisSummary)) {
            var hedisDetailList = { controlledList: [], notControlledList: [], notApplicableList: [], applicableYetToStartList: [], outstandingList: [] };
            hedisMetricsList.forEach((metric, index) => {
                let metricInfo = chartDetails.HedisSummary.find((h) => h.HedisMetricType === metric.type);
                let metricInfoCopy = {
                    ...metricInfo,
                    Label: metric.label,
                    Type: metric.type,
                    IsShow: false,
                };
                if (metricInfo?.IsApplicable) {
                    if (metricInfoCopy?.IsExpired) {
                        hedisDetailList.outstandingList.push(metricInfoCopy);
                    } else if (metricInfoCopy?.MetricStatus === HedisMetricStatusEnum.CompletedAndNotControlled) {
                        hedisDetailList.outstandingList.push(metricInfoCopy);
                    } else if (
                        metricInfoCopy?.MetricStatus === HedisMetricStatusEnum.Completed ||
                        metricInfoCopy?.MetricStatus === HedisMetricStatusEnum.CompletedAndControlled
                    ) {
                        hedisDetailList.controlledList.push(metricInfoCopy);
                    } else {
                        hedisDetailList.outstandingList.push(metricInfoCopy);
                    }
                } else {
                    hedisDetailList.notApplicableList.push(metricInfoCopy);
                }
            });
            return hedisDetailList.outstandingList;
        }
    };

    const hedisDetail = prepareHedisDetail();

    // REQUESTS
    const requestAgeLabel = (createdOn: Date) => {
        let label = '';
        const age = differenceInDays(new Date(), toDate(createdOn) || new Date()).toString();
        if (age === '0') {
            label = 'Today';
        } else if (age === '1') {
            label = `Yesterday`;
        } else {
            label = `${age} days ago`;
        }
        return label;
    };

    // ROLES
    const changeRoles = (role: string, isConfimedChanged = false) => {
        if (
            !chartDetails.IsInHra &&
            !chartDetails.IsInFuhFum &&
            selectedRole === roleEnum.FIELD_ONBOARDING &&
            (chartDetails.Status === MemberStatusEnum.Undetermined || chartDetails.Status === MemberStatusEnum.Scheduled)
        ) {
            if (isConfimedChanged) {
                const meesage = `Provider has started ${role} Assessment when status is in ${MemberStatusEnum[chartDetails.Status]}.`;
                dispatch(insertTimeline(memberId, meesage, TimelineTypeEnum.Assessments));
                setShowStatusChangeAlert(false);
            } else {
                setSelectedRoleId(role);
                setShowStatusChangeAlert(true);
                return;
            }
        }
        dispatch(setSelectedRole(role));
    };

    const rolesWithAssignedAssessments = roles.filter((r) => r.AssignedAssessments?.length > 0);
    const canShowAssessment = rolesWithAssignedAssessments?.length > 0 && rolesWithAssignedAssessments?.some((r) => r.Name === authData.Role);

    const getBHDiagnosticStatus = () => {
        const completedDate = toDate(bhDiagnostic?.CompletedDate);
        const expirationDate = completedDate ? addDays(completedDate, 180) : null;
        const cutoffDate = addDays(new Date(), -180);

        if (completedDate && isBefore(completedDate, cutoffDate)) {
            return `Expired on ${expirationDate ? formatDate(expirationDate, 'MM/dd/yyyy') : ''}`;
        } else if (bhDiagnostic?.DiagnosticAssessmentStatus === StatusEnum.Completed && completedDate && !isBefore(completedDate, cutoffDate)) {
            return `Completed on ${formatDate(completedDate, 'MM/dd/yyyy')}`;
        } else if (bhDiagnostic?.DiagnosticAssessmentStatus === StatusEnum.New) {
            return 'Incomplete';
        } else if (bhDiagnostic?.DiagnosticAssessmentStatus === StatusEnum.InProgress) {
            return 'In Progress';
        } else {
            return 'Incomplete';
        }
    };

    const showBHDiagnosticOption = () => {
        return authData.Permissions.includes('Show BH Diagnostic assessment option') && memberIsValidStatus() ? true : false;
    };

    const handleCloseDocumentDrafts = () => {
        dispatch(toggleIsOpenSavedDraftsModel(false));
    };

    const closeAISessionIdModal = () => {
        setAISessionId('');
        dispatch(toggleIsOpenAISessionId(false));
    };

    const handleCloseAISessionId = () => {
        closeAISessionIdModal();
        setHeidiAiStep(null);
    };

    const submitAISessionId = () => {
        if (heidiAiStep !== 'existing' || !AISessionId.trim()) {
            return;
        }
        dispatch(toggleIsOpenAISessionId(false));
        dispatch(applyAISession(memberId, AISessionId, selectedRole, navigate, diagnosticType));
    };

    const handleOnSelectDraftPreview = (selected: boolean, index: number) => {
        let data = copyObject(engagementDraftPreviews);
        data[index].IsSelected = selected;
        dispatch(setEngagementDraftPreviews(data));
    };

    const handleViewDrafts = () => {
        if (canShowAssessment && roles.length > 0) {
            dispatch(toggleIsOpenSavedDraftsModel(true));
        }
    };

    const handleOnClickDraft = (id: string = null) => {
        setSelectedEngagementDraftId(id);
        navigate(`assessment/${id}`);
    };

    const handleOnClickNew = (role: string, diagnosticType: DiagnosticType) => {
        var isNPFUHFUM = chartDetails?.IsInFuhFum && role === roleEnum.NURSE_PRACTITIONER;
        var isBHFUHUM = chartDetails?.IsInFuhFum && role === roleEnum.BEHAVIOURAL_HEALTH_SPECIALIST;
        role = isNPFUHFUM ? roleEnum.NURSE_PRACTITIONER_FUHFUM : isBHFUHUM ? roleEnum.BEHAVIOURAL_HEALTH_SPECIALIST_FUHFUM : role;
        if (
            assessmentType === AssessmentDashboardEnum.AssessmentAI ||
            assessmentType === AssessmentDashboardEnum.AssessmentAI2 ||
            assessmentType === AssessmentDashboardEnum.BHDiagnosticAI
        ) {
            openHeidiAiWizard();
            return;
        }
        setIsLoading(true);
        dispatch(
            createNewMemberAssessment(memberId, role, diagnosticType, (id) => {
                setIsLoading(false);
                navigate(`assessment/${id}`);
            })
        );
    };

    const openHeidiAiWizard = () => {
        setHeidiAiStartPath('');
        setHeidiAiStep('choose');
        setIsHeidiAiWizardOpen(true);
    };

    const handleCloseHeidiAiWizard = () => {
        setIsHeidiAiWizardOpen(false);
        setHeidiAiStartPath('');
        setHeidiAiStep(null);
    };

    const continueHeidiAiWizard = () => {
        if (heidiAiStartPath === 'existing') {
            setHeidiAiStep('existing');
            setIsHeidiAiWizardOpen(false);
            dispatch(toggleIsOpenAISessionId(true));
            return;
        }
        if (heidiAiStartPath === 'record') {
            setHeidiAiStep('record');
            setIsHeidiAiWizardOpen(false);
            displayAISessionWidget();
        }
    };

    const backToHeidiAiWizard = () => {
        closeAISessionIdModal();
        closeHeidiContextOptionsModal();
        setHeidiAiStep('choose');
        setIsHeidiAiWizardOpen(true);
    };

    const resetHeidiContextOptions = () => {
        setHeidiContextNotes([]);
        setHeidiContextNotesSource('');
        setHeidiContextJourney('');
        setHeidiContextAiSummary('');
        setSelectedHeidiNoteIds([]);
        setIncludeMemberJourney(false);
        setIncludeAiMemberSummary(false);
        setIsHeidiContextOptionsLoading(false);
    };

    const formatHeidiContextNoteLabel = (note: HeidiContextNoteItem): string => {
        const noteDate = toDate(note.date);
        const isProgressNotes = heidiContextNotesSource === 'ProgressNotes';
        if (isProgressNotes) {
            const dateLabel = noteDate && isValid(noteDate) ? formatDate(noteDate, 'MMMM dd, yyyy hh:mm a') : 'Unknown date';
            const providerName = (note.providerName || '').trim() || 'Unknown provider';
            return `Progress Note - ${dateLabel} - ${providerName}`;
        }

        const dateLabel = noteDate && isValid(noteDate) ? formatDate(noteDate, 'MMMM dd, yyyy hh:mm a') : 'Unknown date';
        return note.preview ? `${dateLabel} - ${note.preview}` : dateLabel;
    };

    const closeHeidiContextOptionsModal = () => {
        // Invalidate in-flight context fetches so stale responses cannot overwrite state.
        heidiContextRequestIdRef.current += 1;
        setIsHeidiContextOptionsOpen(false);
        resetHeidiContextOptions();
    };

    const handleCloseHeidiContextOptions = () => {
        closeHeidiContextOptionsModal();
        setHeidiAiStep(null);
    };

    const displayAISessionWidget = async () => {
        const requestId = ++heidiContextRequestIdRef.current;
        setIsHeidiContextOptionsOpen(true);
        setIsHeidiContextOptionsLoading(true);
        setSelectedHeidiNoteIds([]);
        setIncludeMemberJourney(false);
        setIncludeAiMemberSummary(false);
        try {
            const [notesResult, journey, aiSummary] = await Promise.all([
                fetchHeidiContextNotes(memberId),
                fetchMemberJourneySummaryForContext(memberId),
                fetchMemberAiSummaryForContext(memberId),
            ]);
            if (requestId !== heidiContextRequestIdRef.current) {
                return;
            }
            setHeidiContextNotes(
                (notesResult.notes || []).map((n, i) => ({
                    ...n,
                    id: n.id || `note-${i}`,
                }))
            );
            setHeidiContextNotesSource(notesResult.source || '');
            setHeidiContextJourney(journey);
            setHeidiContextAiSummary(aiSummary);
        } catch {
            if (requestId !== heidiContextRequestIdRef.current) {
                return;
            }
            setHeidiContextNotes([]);
            setHeidiContextNotesSource('');
            setHeidiContextJourney('');
            setHeidiContextAiSummary('');
        } finally {
            if (requestId === heidiContextRequestIdRef.current) {
                setIsHeidiContextOptionsLoading(false);
            }
        }
    };

    const buildHeidiContextString = (): string => {
        const sections: string[] = [];
        heidiContextNotes
            .filter((note) => selectedHeidiNoteIds.includes(note.id) && Boolean(note.fullText))
            .forEach((note) => {
                const noteDate = toDate(note.date);
                const dateLabel = noteDate && isValid(noteDate) ? formatDate(noteDate, 'MMMM dd, yyyy hh:mm a') : 'Unknown date';
                const providerName = (note.providerName || '').trim();
                const isProgressNotes = heidiContextNotesSource === 'ProgressNotes';
                const header = isProgressNotes
                    ? `Progress Note - ${dateLabel}${providerName ? ` - ${providerName}` : ''}`
                    : providerName
                    ? `${dateLabel} - ${providerName}`
                    : dateLabel;
                sections.push(`## ${isProgressNotes ? header : `Prior Session Note (${header})`}\n${note.fullText}`);
            });
        if (includeMemberJourney && heidiContextJourney) {
            sections.push(`## Member Journey\n${heidiContextJourney}`);
        }
        if (includeAiMemberSummary && heidiContextAiSummary) {
            sections.push(`## AI Member Summary & Achievements\n${heidiContextAiSummary}`);
        }
        return sections.join('\n\n').trim();
    };

    const submitHeidiContextOptions = async () => {
        if (heidiAiStep !== 'record') {
            return;
        }
        const dob = toDate(chartDetails?.DOB);
        const name = `${chartDetails?.FirstName || ''} ${chartDetails?.LastName || ''}`.trim();
        const patient = {
            id: memberId,
            name: name || undefined,
            gender: chartDetails?.Gender === GenderEnum.Male ? 'Male' : chartDetails?.Gender === GenderEnum.Female ? 'Female' : undefined,
            dob: dob && isValid(dob) ? formatDate(dob, 'yyyy-MM-dd') : undefined,
        };
        const context = buildHeidiContextString();
        heidiContextRequestIdRef.current += 1;
        setIsHeidiContextOptionsOpen(false);
        resetHeidiContextOptions();
        setIsHeidiWidgetLoading(true);
        try {
            await dispatch(launchHeidiWidget(memberId, selectedRole, patient, navigate, context, diagnosticType));
        } finally {
            setIsHeidiWidgetLoading(false);
        }
    };

    const toggleHeidiNoteSelection = (noteId: string, checked: boolean) => {
        setSelectedHeidiNoteIds((prev) => {
            if (checked) {
                return prev.includes(noteId) ? prev : [...prev, noteId];
            }
            return prev.filter((id) => id !== noteId);
        });
    };

    useEffect(() => {
        if (canShowAssessment) {
            dispatch(getEngagementDraftPreview(memberId));
        }
    }, [dispatch, memberId, canShowAssessment]);

    useEffect(() => {
        if (roles.length > 0 && !isFetched && Boolean(chartDetails?.Id)) {
            setRoleList(rolesWithAssignedAssessments);
            setIsFetched(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetched, roles, chartDetails]);

    const initialPage = useCallback(async () => {
        dispatch(fetchRoles());
        dispatch(fetchMemberDemoGraphicInfo(memberId));
        dispatch(getMemberById(memberId));
        dispatch(getCarePlanSignatureStatus(memberId));
        dispatch(fetchPHQ9ByMemberId(memberId));
        dispatch(getMemberByMemberId(memberId));
        dispatch(getConsentFormDocumentByMemberId(memberId));
        dispatch(getMemberRequests(memberId));
        dispatch(getMemberSurveys(memberId));
        dispatch(resetAssessment());
        dispatch(resetHraAssessment());
        dispatch(resetFuhFumAssessment());
        dispatch(resetDocumentEngagement());
    }, [dispatch, memberId]);

    useEffect(() => {
        dispatch(getLastCompletedBHDiagnosticByMemberId(memberId, selectedRole));
    }, [memberId, selectedRole, dispatch]);

    useEffect(() => {
        const authRole = roleList.some((r) => r.Name === authData.Role) ? authData.Role : '';
        let role = '';
        if (
            chartDetails &&
            chartDetails?.IsInTrueBalance &&
            (chartDetails?.Status === MemberStatusEnum.Undetermined || chartDetails?.Status === MemberStatusEnum.Scheduled)
        ) {
            role = roleEnum.TRUE_BALANCE;
        } else if (
            chartDetails &&
            !chartDetails?.IsInHra &&
            !chartDetails?.IsInFuhFum &&
            (chartDetails?.Status === MemberStatusEnum.Undetermined || chartDetails?.Status === MemberStatusEnum.Scheduled)
        ) {
            role = roleEnum.FIELD_ONBOARDING;
        } else {
            const isFuhfumAssessmentShow =
                chartDetails?.IsInFuhFum && authData.Role !== roleEnum.NURSE_PRACTITIONER && authData.Role !== roleEnum.BEHAVIOURAL_HEALTH_SPECIALIST;
            role = role || (chartDetails?.IsInHra ? roleEnum.HRA : isFuhfumAssessmentShow ? roleEnum.TRANSITIONAL_CARE_COORDINATOR : authRole);
            if (!Boolean(role)) {
                role = roleEnum.CEO;
            }
        }
        dispatch(setSelectedRole(role));
    }, [dispatch, roleList, chartDetails, authData.Role]);

    useEffect(() => {
        if (isFetched) {
            return;
        }
        initialPage();
    }, [initialPage, isFetched]);

    const renderDocumentDrafts = () => {
        return (
            <Dialog
                open={isOpenSavedDraftsModel}
                onClose={handleCloseDocumentDrafts}
                scroll="paper"
                maxWidth="sm"
                fullWidth
                sx={{ scrollPaper: classes.topScrollPaper, paperScrollBody: classes.topPaperScrollBody }}
                TransitionComponent={Transition}
            >
                <DialogContent dividers={true}>
                    <DialogContentText id="scroll-dialog-content" tabIndex={-1} fontSize={14}>
                        There are assessments available in drafts.
                    </DialogContentText>
                    <Button
                        variant="contained"
                        color="info"
                        sx={{ marginY: 2 }}
                        className="text-transform-none"
                        size="small"
                        onClick={() => setShowDeleteAlert(true)}
                        disabled={!engagementDraftPreviews.some((d) => d.IsSelected)}
                    >
                        Delete Selected Draft{isMultiDraftsSelected ? 's' : ''}
                    </Button>
                    {engagementDraftPreviews.map((draft, index) => (
                        <Stack key={`saved-draft${draft.Id}`} direction="row" spacing={0} alignItems="center" style={{ marginLeft: '-10px' }}>
                            <Checkbox checked={draft.IsSelected} onChange={(e) => handleOnSelectDraftPreview(e.target.checked, index)} />
                            <Link underline="none" onClick={() => handleOnClickDraft(draft.Id)}>
                                {`Draft ${index + 1} - ${(() => {
                                    const d = toDate(draft?.CreatedDate);
                                    return d ? formatDate(d, 'MMMM dd, yyyy hh:mm a') : '';
                                })()}${draft?.DiagnosticType !== DiagnosticType.None ? ` - ${DiagnosticType[draft?.DiagnosticType]}` : ''}`}
                            </Link>
                            {draft.IsAiCreated && <Chip label="AI Session - Processing Complete" size="small" color="success" sx={{ marginLeft: 1 }} />}
                            {selectedEngagementDraftId === draft.Id ? <CircularProgress size={16} className="ml-2" /> : null}
                        </Stack>
                    ))}
                    {engagementDraftPreviews.length === 0 && (
                        <Box sx={{ padding: 2, textAlign: 'center' }}>
                            <Typography variant="body2">No Drafts Available</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleCloseDocumentDrafts} className="text-transform-none">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const renderHeidiApplyProgress = () => {
        return (
            <Dialog open={isHeidiApplyInProgress} maxWidth="sm" fullWidth>
                <DialogContent>
                    <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CircularProgress size={18} />
                            <Typography variant="h6">Applying Heidi Session</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            This may take several minutes. You will be taken to the assessments for review when finished, please review carefully.
                        </Typography>
                        <Stack spacing={1}>
                            <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, heidiApplyProgressPercentage || 0))} />
                            <Typography variant="body2" color="text.secondary">
                                {Math.round(heidiApplyProgressPercentage || 0)}% ({heidiApplyCompletedAssessments}/{heidiApplyTotalAssessments || 0})
                            </Typography>
                        </Stack>
                        <Typography variant="body2">Last assessment processed: {heidiApplyLastCompletedAssessment || '-'}</Typography>
                    </Stack>
                </DialogContent>
            </Dialog>
        );
    };

    const renderHeidiAiWizard = () => {
        return (
            <Dialog
                open={isHeidiAiWizardOpen}
                onClose={handleCloseHeidiAiWizard}
                scroll="paper"
                maxWidth="sm"
                fullWidth
                sx={{ scrollPaper: classes.topScrollPaper, paperScrollBody: classes.topPaperScrollBody }}
                TransitionComponent={Transition}
            >
                <DialogContent dividers={true}>
                    <DialogContentText tabIndex={-1} fontSize={14} component="div">
                        <Typography style={{ fontWeight: 'bolder' }}>
                            {assessmentType === AssessmentDashboardEnum.BHDiagnosticAI ? 'BH Assessment with AI 2.0' : 'Assessment with AI 2.0'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Step 1 of 2 — Choose how you want to start this Heidi session.
                        </Typography>
                    </DialogContentText>
                    <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
                        <RadioGroup
                            value={heidiAiStartPath}
                            onChange={(e) => setHeidiAiStartPath(e.target.value as 'existing' | 'record')}
                        >
                            <Paper
                                variant="outlined"
                                sx={{ p: 2, mb: 1.5, cursor: 'pointer', borderColor: heidiAiStartPath === 'existing' ? 'primary.main' : undefined }}
                                onClick={() => setHeidiAiStartPath('existing')}
                            >
                                <FormControlLabel
                                    value="existing"
                                    control={<Radio />}
                                    label={
                                        <Stack spacing={0.5}>
                                            <Typography variant="body1" fontWeight="bold">
                                                Enter an existing Heidi AI Session ID
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Use a session that has already been recorded in Heidi.
                                            </Typography>
                                        </Stack>
                                    }
                                />
                            </Paper>
                            <Paper
                                variant="outlined"
                                sx={{ p: 2, cursor: 'pointer', borderColor: heidiAiStartPath === 'record' ? 'primary.main' : undefined }}
                                onClick={() => setHeidiAiStartPath('record')}
                            >
                                <FormControlLabel
                                    value="record"
                                    control={<Radio />}
                                    label={
                                        <Stack spacing={0.5}>
                                            <Typography variant="body1" fontWeight="bold">
                                                Record a new Heidi Session
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Start a new recording and choose context to include.
                                            </Typography>
                                        </Stack>
                                    }
                                />
                            </Paper>
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={continueHeidiAiWizard}
                        className="text-transform-none"
                        disabled={!heidiAiStartPath}
                    >
                        Continue
                    </Button>
                    <Button variant="outlined" onClick={handleCloseHeidiAiWizard} className="text-transform-none">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const renderHeidiContextOptions = () => {
        const hasJourney = Boolean(heidiContextJourney);
        const hasAiSummary = Boolean(heidiContextAiSummary);
        const hasNotes = heidiContextNotes.length > 0;
        const hasAnyOption = hasNotes || hasJourney || hasAiSummary;

        return (
            <Dialog
                open={isHeidiContextOptionsOpen}
                onClose={handleCloseHeidiContextOptions}
                scroll="paper"
                maxWidth="sm"
                fullWidth
                sx={{ scrollPaper: classes.topScrollPaper, paperScrollBody: classes.topPaperScrollBody }}
                TransitionComponent={Transition}
            >
                <DialogContent dividers={true}>
                    <DialogContentText tabIndex={-1} fontSize={14} component="div">
                        <Typography style={{ fontWeight: 'bolder' }}>Context Options to include</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Step 2 of 2 — Select the context to include when recording a new Heidi session.
                        </Typography>
                    </DialogContentText>
                    <Box sx={{ marginTop: 2 }}>
                        {isHeidiContextOptionsLoading ? (
                            <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
                                <CircularProgress size={28} />
                                <Typography variant="body2">Loading context options…</Typography>
                            </Stack>
                        ) : !hasAnyOption ? (
                            <Alert severity="info" sx={{ mt: 1 }}>
                                No context options are available for this member. You can still Submit to open Heidi without additional context.
                            </Alert>
                        ) : (
                            <Stack spacing={1} sx={{ mt: 1 }}>
                                {heidiContextNotes.map((note) => {
                                    const label = formatHeidiContextNoteLabel(note);
                                    return (
                                        <FormControlLabel
                                            key={note.id}
                                            control={
                                                <Checkbox
                                                    checked={selectedHeidiNoteIds.includes(note.id)}
                                                    onChange={(e) => toggleHeidiNoteSelection(note.id, e.target.checked)}
                                                />
                                            }
                                            label={<Typography variant="body2">{label}</Typography>}
                                        />
                                    );
                                })}
                                {hasJourney && (
                                    <FormControlLabel
                                        control={<Checkbox checked={includeMemberJourney} onChange={(e) => setIncludeMemberJourney(e.target.checked)} />}
                                        label={<Typography variant="body2">Member Journey</Typography>}
                                    />
                                )}
                                {hasAiSummary && (
                                    <FormControlLabel
                                        control={<Checkbox checked={includeAiMemberSummary} onChange={(e) => setIncludeAiMemberSummary(e.target.checked)} />}
                                        label={<Typography variant="body2">AI Member Summary & Achievements</Typography>}
                                    />
                                )}
                            </Stack>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={backToHeidiAiWizard} className="text-transform-none">
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={submitHeidiContextOptions}
                        className="text-transform-none"
                        disabled={isHeidiContextOptionsLoading || isHeidiApplyInProgress || isHeidiWidgetLoading}
                    >
                        Submit
                    </Button>
                    <Button variant="outlined" onClick={handleCloseHeidiContextOptions} className="text-transform-none">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const renderAISessionId = () => {
        return (
            <Dialog
                open={isOpenAISessionId}
                onClose={handleCloseAISessionId}
                scroll="paper"
                maxWidth="sm"
                fullWidth
                sx={{ scrollPaper: classes.topScrollPaper, paperScrollBody: classes.topPaperScrollBody }}
                TransitionComponent={Transition}
            >
                <DialogContent dividers={true}>
                    <DialogContentText id="scroll-dialog-content" tabIndex={-1} fontSize={14} component="div">
                        <Typography style={{ fontWeight: 'bolder' }}>Please enter your AI Session ID to create a new assessment.</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Step 2 of 2 — Enter an existing Heidi AI Session ID.
                        </Typography>
                    </DialogContentText>
                    <Box sx={{ marginTop: 2 }}></Box>
                    <Alert icon={<InfoIcon fontSize="inherit" />} severity="info">
                        This will take 10-15 minutes to process, so please be patient.
                    </Alert>
                    <Box sx={{ marginTop: 4, marginBottom: 4 }}>
                        <FormControl fullWidth>
                            <TextField label="AI Session ID" required value={AISessionId} onChange={(e) => setAISessionId(e.target.value)} fullWidth />
                        </FormControl>
                    </Box>
                    <FormControl fullWidth>
                        <InputLabel id="assessment-role-select-label">Role</InputLabel>
                        <Select labelId="assessment-role-select-label" label="Role" value={selectedRole} onChange={(event) => changeRoles(event.target.value)}>
                            {roleList
                                .filter((r) => r.Id)
                                .map((type, index) => (
                                    <MenuItem key={`${type.Id}-${index}`} value={type.Name}>
                                        {type.Name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={backToHeidiAiWizard} className="text-transform-none">
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={submitAISessionId}
                        className="text-transform-none"
                        disabled={isHeidiApplyInProgress || !AISessionId.trim()}
                    >
                        Submit
                    </Button>
                    <Button variant="outlined" onClick={handleCloseAISessionId} className="text-transform-none">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const renderSignConsentModal = () => {
        return (
            <Dialog
                open={isOpenSignConsentModel}
                onClose={() => dispatch(handleCloseSignConsentModal())}
                scroll="paper"
                maxWidth="sm"
                fullWidth
                sx={{ scrollPaper: classes.topScrollPaper, paperScrollBody: classes.topPaperScrollBody }}
                TransitionComponent={Transition}
            >
                <DialogContent dividers={true}>
                    <Consent memberId={memberId} />
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => dispatch(handleCloseSignConsentModal())} sx={{ minWidth: '120px' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    return isFetched && !isLoading ? (
        <Grid container spacing={2} sx={{ padding: 2 }}>
            <Loading message={'Processing...'} />
            <MessageDisplay />
            {renderHeidiApplyProgress()}
            {renderHeidiAiWizard()}
            {renderHeidiContextOptions()}
            {renderAISessionId()}
            {renderDocumentDrafts()}
            {renderSignConsentModal()}
            <AssessmentHistory memberId={memberId} open={showHistory} canShowDrafts={canShowAssessment} onClose={() => setShowHistory(false)} />
            <AlertDialog
                open={showDeleteAlert}
                onConfirm={() => {
                    setShowDeleteAlert(false);
                    dispatch(deleteAssessmentDrafts(engagementDraftPreviews.filter((e) => e.IsSelected).map((e) => e.Id)));
                }}
                onClose={() => setShowDeleteAlert(false)}
                message={`Are you sure you want to delete the selected draft${isMultiDraftsSelected ? 's' : ''}?`}
            />
            <AlertDialog
                open={showStatusChangeAlert}
                onConfirm={() => changeRoles(selectedRoleId, true)}
                okText="Yes"
                cancelText="No"
                onClose={() => setShowStatusChangeAlert(false)}
                message={`You have chosen to change the role for this assessment. This member is not yet in active status and requires an onboarding assessment available only under the field onboarding role. Do you still wish to proceed?`}
            />
            <Grid size={12}>
                <Typography variant="h5">Assessment Dashboard</Typography>
                <Divider sx={{ margin: '16px 0' }} />
            </Grid>
            <Grid size={12}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Stack spacing={2}>
                            <FormControl fullWidth>
                                <InputLabel id="assessment-role-select-label">Role</InputLabel>
                                <Select
                                    labelId="assessment-role-select-label"
                                    label="Role"
                                    value={selectedRole}
                                    onChange={(event) => changeRoles(event.target.value)}
                                >
                                    {roleList
                                        .filter((r) => r.Id)
                                        .filter(
                                            (r) => r.Name !== roleEnum.BEHAVIOURAL_HEALTH_SPECIALIST_FUHFUM && r.Name !== roleEnum.NURSE_PRACTITIONER_FUHFUM
                                        )
                                        .map((type, index) => (
                                            <MenuItem key={`${type.Id}-${index}`} value={type.Name}>
                                                {type.Name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="assessment-type-select-label">Assessment Type</InputLabel>
                                <Select
                                    labelId="assessment-type-select-label"
                                    label="Assessment Type"
                                    value={assessmentType}
                                    onChange={handleAssessmentTypeChange}
                                >
                                    <MenuItem value={AssessmentDashboardEnum.Assessment}>Assessment</MenuItem>
                                    <MenuItem value={AssessmentDashboardEnum.AssessmentAI}>Assessment with AI 2.0</MenuItem>
                                    {showBHDiagnosticOption() && <MenuItem value={AssessmentDashboardEnum.BHDiagnostic}>BH Diagnostic</MenuItem>}
                                    {showBHDiagnosticOption() && <MenuItem value={AssessmentDashboardEnum.BHDiagnosticAI}>BH Assessment with AI 2.0</MenuItem>}
                                </Select>
                            </FormControl>
                            <Button
                                variant="contained"
                                size="large"
                                color="success"
                                onClick={() => handleOnClickNew(selectedRole, diagnosticType)}
                                disabled={!canShowAssessment}
                            >
                                Start{' '}
                            </Button>
                            {showBHDiagnosticOption() && (
                                <Stack alignItems={'center'} spacing={1} width={'100%'} justifyContent={'center'} pt={2}>
                                    <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                        {bhDiagnostic?.DiagnosticAssessmentStatus === StatusEnum.Completed &&
                                        (() => {
                                            const d = toDate(bhDiagnostic?.CompletedDate);
                                            const cutoff = addDays(new Date(), -180);
                                            return d && !isBefore(d, cutoff);
                                        })() ? (
                                            <Tooltip title="Complete">
                                                <CheckCircleIcon color="success" />
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title={getBHDiagnosticStatus()}>
                                                <CancelIcon color="error" />
                                            </Tooltip>
                                        )}
                                        <Typography textAlign={'center'} variant="body1">
                                            BH Diagnostic:
                                        </Typography>
                                    </Stack>
                                    <Typography component="div" textAlign={'center'} variant="caption" color="text.secondary" fontWeight="regular">
                                        <span>{getBHDiagnosticStatus()}</span>
                                    </Typography>
                                    <Tooltip title="BH Diagnostic Help Document">
                                        <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${BHDHelp}`, '_blank')} />
                                    </Tooltip>
                                </Stack>
                            )}

                            <Stack direction="row" spacing={1} py={5}>
                                <Button
                                    fullWidth
                                    disabled={!canShowAssessment && roles.length > 0}
                                    variant="outlined"
                                    size="small"
                                    color="inherit"
                                    onClick={handleViewDrafts}
                                >
                                    Drafts {engagementDraftPreviews.length > 0 && `(${engagementDraftPreviews.length})`}
                                </Button>
                                <Button fullWidth size="small" variant="outlined" color="inherit" onClick={() => setShowHistory(true)}>
                                    History
                                </Button>
                            </Stack>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, md: 9 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        padding: 2,
                                        textAlign: 'center',
                                        height: '250px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography variant="h5" color="primary.main">
                                        Consent
                                    </Typography>
                                    <Stack justifyContent={'center'} alignItems={'center'} spacing={1} mt={1}>
                                        <Stack alignItems={'center'} justifyContent={'center'} spacing={0} mt={5}>
                                            <Stack direction="row" alignItems={'center'} spacing={1}>
                                                <Typography variant="body1">Part One:</Typography>
                                                {partOneDocument?.IsExpired &&
                                                Boolean(partOneDocument?.ConsentFormDocument?.DocumentSignedOn) &&
                                                (() => {
                                                    const d = toDate(partOneDocument?.ConsentFormDocument?.DocumentSignedOn);
                                                    return d && isValid(d);
                                                })() ? (
                                                    <Chip label="Expired" size="small" color="error" style={{ marginLeft: '5px' }} />
                                                ) : Boolean(partOneDocument?.ConsentFormDocument?.DocumentSignedOn) &&
                                                  (() => {
                                                      const d = toDate(partOneDocument?.ConsentFormDocument?.DocumentSignedOn);
                                                      return d && isValid(d);
                                                  })() ? (
                                                    <Tooltip title="Complete">
                                                        <CheckCircleIcon color="success" />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Missing">
                                                        <CancelIcon color="error" />
                                                    </Tooltip>
                                                )}
                                            </Stack>

                                            <Typography component="div" variant="caption" color="text.secondary" fontWeight="regular">
                                                <div>
                                                    <span>
                                                        {Boolean(partOneDocument?.ConsentFormDocument?.DocumentSignedOn) &&
                                                        (() => {
                                                            const d = toDate(partOneDocument?.ConsentFormDocument?.DocumentSignedOn);
                                                            return d && isValid(d);
                                                        })()
                                                            ? `Document Signed On: ${(() => {
                                                                  const d = toDate(partOneDocument?.ConsentFormDocument?.DocumentSignedOn);
                                                                  return d ? formatDate(d, 'MM/dd/yyyy') : '';
                                                              })()}`
                                                            : 'Not Uploaded Yet'}
                                                    </span>
                                                </div>
                                            </Typography>
                                        </Stack>
                                        <Stack alignItems={'center'} justifyContent={'center'} spacing={0} mt={2}>
                                            <Stack direction="row" alignItems={'center'} spacing={1}>
                                                <Typography variant="body1">Part Two:</Typography>
                                                {partTwoDocument?.IsExpired &&
                                                Boolean(partTwoDocument?.ConsentFormDocument?.DocumentSignedOn) &&
                                                (() => {
                                                    const d = toDate(partTwoDocument?.ConsentFormDocument?.DocumentSignedOn);
                                                    return d && isValid(d);
                                                })() ? (
                                                    <Chip label="Expired" size="small" color="error" style={{ marginLeft: '5px' }} />
                                                ) : Boolean(partTwoDocument?.ConsentFormDocument?.DocumentSignedOn) &&
                                                  (() => {
                                                      const d = toDate(partTwoDocument?.ConsentFormDocument?.DocumentSignedOn);
                                                      return d && isValid(d);
                                                  })() ? (
                                                    <Tooltip title="Complete">
                                                        <CheckCircleIcon color="success" />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Missing">
                                                        <CancelIcon color="error" />
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                            <Typography component="div" variant="caption" color="text.secondary" fontWeight="regular">
                                                <div>
                                                    <span>
                                                        {Boolean(partTwoDocument?.ConsentFormDocument?.DocumentSignedOn) &&
                                                        (() => {
                                                            const d = toDate(partTwoDocument?.ConsentFormDocument?.DocumentSignedOn);
                                                            return d && isValid(d);
                                                        })()
                                                            ? `Document Signed On: ${(() => {
                                                                  const d = toDate(partTwoDocument?.ConsentFormDocument?.DocumentSignedOn);
                                                                  return d ? formatDate(d, 'MM/dd/yyyy') : '';
                                                              })()}`
                                                            : 'Not Uploaded Yet'}
                                                    </span>
                                                </div>
                                            </Typography>
                                        </Stack>
                                        <Button variant="contained" size="small" color="primary" sx={{ minWidth: '120px' }} onClick={handleOpenConsent}>
                                            Sign
                                        </Button>
                                    </Stack>
                                </Paper>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        padding: 2,
                                        textAlign: 'center',
                                        height: '250px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography variant="h5" color="primary.main">
                                        HEDIS
                                    </Typography>
                                    <Stack alignItems={'center'} justifyContent={'center'} spacing={1} mt={5}>
                                        <Typography variant="body1">Outstanding metrics:</Typography>
                                        <Tooltip
                                            title={
                                                <List>
                                                    {hedisDetail?.map((h) => {
                                                        return (
                                                            <ListItem key={h.Id} sx={{ padding: 0 }}>
                                                                <ListItemText primary={h.Label} />
                                                            </ListItem>
                                                        );
                                                    })}
                                                </List>
                                            }
                                        >
                                            <Typography variant="h4">{hedisDetail?.length || 0}</Typography>
                                        </Tooltip>
                                    </Stack>
                                </Paper>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        padding: 2,
                                        textAlign: 'center',
                                        height: '250px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="h5" color="primary.main">
                                        PHQ9
                                    </Typography>
                                    {isFetchingMemberPHQ9 ? (
                                        <Box display="flex" justifyContent="center" sx={{ padding: 2 }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : (
                                        <Stack spacing={1}>
                                            <Typography variant="h4">{memberPHQ9.PHQ9Score}</Typography>
                                            {memberPHQ9.PHQ9Score > 14 && (
                                                <Stack color={'error.main'} direction="row" alignItems={'center'} justifyContent={'center'} spacing={1}>
                                                    <Typography variant="body1">High PHQ9</Typography>
                                                    <WarningIcon />
                                                </Stack>
                                            )}
                                            {differenceInDays(new Date(), toDate(memberPHQ9.AssessmentDate) || new Date()) > 90 && (
                                                <Stack color={'error.main'} direction="row" alignItems={'center'} justifyContent={'center'} spacing={1}>
                                                    <Typography variant="body1">Expired</Typography>
                                                    <WarningIcon />
                                                </Stack>
                                            )}
                                        </Stack>
                                    )}
                                    <Stack>
                                        <Typography variant="body2">Last Completed : </Typography>
                                        {Boolean(memberPHQ9?.AssessmentBy) ? (
                                            <Typography variant="body2" fontStyle={'italic'}>
                                                {memberPHQ9.AssessmentBy?.Name ?? '-'} -{' '}
                                                {(() => {
                                                    const d = toDate(memberPHQ9.AssessmentDate);
                                                    return d ? formatDate(d, 'MM/dd/yyyy') : '';
                                                })()}
                                            </Typography>
                                        ) : (
                                            ' - '
                                        )}
                                    </Stack>
                                </Paper>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        padding: 2,
                                        textAlign: 'center',
                                        height: '250px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="h5" color="primary.main">
                                        Care Plan
                                    </Typography>
                                    {isFetchingCarePlanStatus ? (
                                        <Box display="flex" justifyContent="center" sx={{ padding: 2 }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : (
                                        <Stack spacing={2} alignItems={'center'}>
                                            <Stack direction="row" alignItems={'center'} spacing={1}>
                                                <Typography variant="body1">Member signature</Typography>
                                                {carePlanSignatureStatus?.MemberSignature.SignatureStatus === 'Complete' ? (
                                                    <Tooltip title="Complete">
                                                        <CheckCircleIcon color="success" />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title={carePlanSignatureStatus?.MemberSignature.SignatureStatus}>
                                                        <CancelIcon color="error" />
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                            <Stack direction="row" alignItems={'center'} spacing={1}>
                                                <Typography variant="body1">Provider signature</Typography>
                                                {carePlanSignatureStatus?.ProviderSignature.SignatureStatus === 'Complete' ? (
                                                    <Tooltip title="Complete">
                                                        <CheckCircleIcon color="success" />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title={carePlanSignatureStatus?.ProviderSignature.SignatureStatus}>
                                                        <CancelIcon color="error" />
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </Stack>
                                    )}
                                    <Stack>
                                        <Typography variant="body2">Last Completed : </Typography>
                                        {Boolean(carePlanSignatureStatus?.ProviderSignature.LastReviewedBy) ? (
                                            <Typography variant="body2" fontStyle={'italic'}>
                                                {carePlanSignatureStatus?.ProviderSignature.LastReviewedBy?.Name ?? '-'} -{' '}
                                                {(() => {
                                                    const d = toDate(carePlanSignatureStatus?.ProviderSignature.SignatureDate);
                                                    return d ? formatDate(d, 'MM/dd/yyyy') : '';
                                                })()}
                                            </Typography>
                                        ) : (
                                            ' - '
                                        )}
                                    </Stack>
                                </Paper>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        padding: 2,
                                        textAlign: 'center',
                                        height: '250px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography variant="h5" color="primary.main">
                                        Completed Surveys
                                    </Typography>
                                    {completedSurveys?.length > 0 ? (
                                        <List sx={{ padding: 0, marginTop: 2, overflowY: 'auto' }}>
                                            {completedSurveys.map((survey) => (
                                                <ListItem divider key={survey.Id} sx={{ padding: 0, textAlign: 'center' }}>
                                                    <ListItemText
                                                        primary={`Appointment date: ${(() => {
                                                            const d = toDate(survey.AppointmentDate);
                                                            return d ? formatDate(d, 'MM/dd/yyyy') : '';
                                                        })()}`}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : (
                                        <Typography variant="body1" mt={5} color="text.secondary">
                                            No completed surveys
                                        </Typography>
                                    )}
                                </Paper>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        padding: 2,
                                        textAlign: 'center',
                                        height: '250px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography variant="h5" color="primary.main">
                                        Requests
                                    </Typography>

                                    {openRequests?.length > 0 ? (
                                        <List sx={{ padding: 0, marginTop: 2, overflowY: 'auto' }}>
                                            {openRequests.map((request) => (
                                                <ListItem divider key={request.Id} sx={{ padding: 0, textAlign: 'center' }}>
                                                    <Tooltip title={<RequestListItem request={request} />} placement="left" arrow>
                                                        <ListItemText
                                                            primary={request.RequestTypeLabel}
                                                            secondary={requestAgeLabel(request.CreatedOn) ?? '-'}
                                                        />
                                                    </Tooltip>
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : (
                                        <Typography variant="body1" mt={5} color="text.secondary">
                                            No outstanding requests
                                        </Typography>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    ) : (
        <Box display="flex" justifyContent="center" sx={classes.loadingContainer}>
            <CircularProgress />
        </Box>
    );
};

export default AssessmentDashboard;
